import React, { useState, useEffect } from 'react';
import { Truck } from 'lucide-react';

const loadingMessages = [
  "Loading route",
  "Finding best roads",
  "Calculating distance",
  "Analyzing traffic",
  "Optimizing path",
  "Mapping waypoints"
];

const MapLoader: React.FC = () => {
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 1500);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="absolute inset-0 bg-background/95 backdrop-blur-sm flex flex-col items-center justify-center z-10">
      {/* Road Container */}
      <div className="relative w-full max-w-md mb-8">
        {/* Road */}
        <div className="relative h-24 bg-gradient-to-b from-muted/50 to-muted rounded-lg overflow-hidden">
          {/* Road Markings */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-full h-1 border-t-2 border-dashed border-muted-foreground/30 animate-[scroll_2s_linear_infinite]" 
                 style={{
                   backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 20px, hsl(var(--muted-foreground) / 0.3) 20px, hsl(var(--muted-foreground) / 0.3) 40px)',
                   backgroundSize: '60px 2px',
                   animation: 'scroll 2s linear infinite'
                 }}
            />
          </div>
          
          {/* Animated Truck */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative animate-[bounce_2s_ease-in-out_infinite]">
              <div className="relative">
                {/* Truck Icon with Gradient */}
                <div className="relative">
                  <Truck 
                    className="w-16 h-16 text-lr-cyan animate-pulse-slow" 
                    style={{
                      filter: 'drop-shadow(0 0 8px hsl(var(--lr-cyan) / 0.5))'
                    }}
                  />
                  {/* Wheels animation effect */}
                  <div className="absolute -bottom-1 left-2 w-3 h-3 bg-lr-cyan-dark rounded-full animate-spin" 
                       style={{ animationDuration: '0.5s' }} 
                  />
                  <div className="absolute -bottom-1 right-2 w-3 h-3 bg-lr-cyan-dark rounded-full animate-spin" 
                       style={{ animationDuration: '0.5s' }} 
                  />
                </div>
                
                {/* Speed lines */}
                <div className="absolute -left-6 top-1/2 -translate-y-1/2 flex flex-col gap-1 opacity-60">
                  <div className="h-0.5 w-4 bg-lr-cyan animate-[slide-left_0.5s_ease-in-out_infinite]" />
                  <div className="h-0.5 w-3 bg-lr-cyan animate-[slide-left_0.6s_ease-in-out_infinite]" />
                  <div className="h-0.5 w-2 bg-lr-cyan animate-[slide-left_0.7s_ease-in-out_infinite]" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Loading Text with Flip Animation */}
      <div className="relative h-8 flex items-center justify-center">
        <div className="relative flip-container">
          <div 
            key={messageIndex}
            className="text-lg font-semibold bg-gradient-to-r from-lr-cyan via-lr-cyan-light to-lr-cyan bg-clip-text text-transparent flip-text flip-in"
          >
            {loadingMessages[messageIndex]}...
          </div>
        </div>
      </div>

      {/* Animated dots */}
      <div className="flex gap-2 mt-6">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="w-2 h-2 rounded-full bg-lr-cyan animate-pulse"
            style={{
              animationDelay: `${i * 0.2}s`,
              animationDuration: '1.4s'
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default MapLoader;
